import { useEffect, useState } from "react";
import DeliveryChannelCard from "./DeliveryChannelCard";
import ChannelSequenceSummary from "./ChannelSequenceSummary";
import {
  DeliveryService,
  type LaunchDeliveryConfig,
  type ProviderAccount,
} from "@services/delivery/deliveryService";

/*
    Switching email on for a campaign (MPN-1716).

    It sits beside Publishers rather than inside it, for the same reason direct mail does:
    email is not a publisher. There is no ad account, no bidding and no template to import.
    What it needs instead is whose SES account sends it and what From address recipients see,
    and neither has an equivalent on a network row.

    Switching it on is refused until the From address is set. Refusing here is the point: the
    person who ticked the box is still sitting here and can fix it, while a publish job failing
    at four in the morning is read by nobody.

    The From address is the whole reputation story in one field. It is the client's subdomain
    off our parent domain, so the institution's name is what a student sees while the DKIM,
    SPF and DMARC underneath belong to us. Getting it wrong does not fail loudly; it lands in
    spam, which looks like nothing happening.
*/

const CHANNEL = "email";

interface EmailCardProps {
  launchId: number;
  engagementId: number;
  organizationId?: number | null;
}

export default function EmailCard({ launchId, engagementId, organizationId }: EmailCardProps) {
  const [config, setConfig] = useState<LaunchDeliveryConfig | null>(null);
  const [accounts, setAccounts] = useState<ProviderAccount[]>([]);
  const [sender, setSender] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const service = new DeliveryService();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      service.listLaunchChannels(launchId),
      organizationId ? service.listAccounts(organizationId) : Promise.resolve([]),
    ])
      .then(([channels, orgAccounts]) => {
        if (cancelled) return;
        const found = channels.find((c) => c.channel === CHANNEL) ?? null;
        setConfig(found);
        setSender({
          name: (found?.from_address as any)?.name ?? "",
          email: (found?.from_address as any)?.email ?? "",
        });
        // Only SES accounts. An organisation can hold a Lob and a Twilio account too, and
        // offering those here would let somebody pick a printer to send email with.
        setAccounts(orgAccounts.filter((a) => a.provider === "ses"));
      })
      .catch(() => !cancelled && setConfig(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [launchId, organizationId]);

  const save = async (patch: {
    provider_account_id?: number;
    from_address?: { name: string; email: string };
  }) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.configureChannel(launchId, CHANNEL, patch as any));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the email settings.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (nextEnabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      setConfig(await service.toggleChannel(launchId, CHANNEL, nextEnabled));
    } catch (e) {
      // The server refuses an incomplete channel and says what is missing. That message is
      // more useful than anything this component could invent.
      setError(e instanceof Error ? e.message : "Could not change email.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const enabled = !!config?.is_enabled;
  const ready = !!config?.provider_account_id && !!sender.email;

  return (
    <>
      <DeliveryChannelCard label="Email" enabled={enabled} error={error}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="fw-semibold">
              <i className="bi bi-envelope-at me-2" style={{ color: "#0ea5e9" }}></i>
              One email per person
            </div>
            <div className="text-muted small">
              Sent when this campaign publishes, to everyone with an address who is not in the
              holdout and has not unsubscribed.
            </div>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="email-toggle"
              aria-label="Email"
              checked={enabled}
              disabled={busy || (!enabled && !ready)}
              onChange={(e) => toggle(e.target.checked)}
            />
          </div>
        </div>

        {!ready && (
          <div className="alert alert-warning py-2 small mb-3">
            Pick an account and set a From address before switching email on.
          </div>
        )}

        {/* CAN-SPAM requires a physical mailing address in every marketing email, and the
            footer takes it from the sending account. Inline, because it does not stop the
            channel being switched on -- it stops the mail being lawful (MPN-2203). */}
        {enabled && config?.sender_address_source === "none" && (
          <div className="alert alert-warning py-2 small mb-3">
            No mailing address for this campaign or its organization. Every marketing email
            has to carry one in the footer. Set it on the sending account.
          </div>
        )}

        <div className="mb-3">
          <label className="form-label small text-muted mb-1" htmlFor="email-account">
            Account that sends it
          </label>
          <select
            id="email-account"
            className="form-select form-select-sm"
            value={config?.provider_account_id ?? ""}
            disabled={busy}
            onChange={(e) => save({ provider_account_id: Number(e.target.value) })}
          >
            <option value="">Choose an account…</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          {accounts.length === 0 && (
            <div className="form-text">
              No email account for this organization yet. Add one in Delivery Setup.
            </div>
          )}
        </div>

        <div>
          <div className="small text-muted mb-2">
            Who it comes from. Recipients see this, and replies go to it.
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <div style={{ minWidth: 180, flex: "1 1 auto" }}>
              <label className="form-label small text-muted mb-1" htmlFor="email-from-name">
                Name
              </label>
              <input
                id="email-from-name"
                className="form-control form-control-sm"
                value={sender.name}
                placeholder="Jefferson CC"
                onChange={(e) => setSender({ ...sender, name: e.target.value })}
                onBlur={() => save({ from_address: sender })}
              />
            </div>
            <div style={{ minWidth: 260, flex: "2 1 auto" }}>
              <label className="form-label small text-muted mb-1" htmlFor="email-from-address">
                Address
              </label>
              <input
                id="email-from-address"
                className="form-control form-control-sm"
                value={sender.email}
                placeholder="updates@jeffersoncc.example.com"
                onChange={(e) => setSender({ ...sender, email: e.target.value })}
                onBlur={() => save({ from_address: sender })}
              />
              <div className="form-text">
                The client&apos;s subdomain off our sending domain, so their name is what
                students see while the authentication underneath stays ours.
              </div>
            </div>
          </div>
        </div>
        <ChannelSequenceSummary
          launchId={launchId}
          engagementId={engagementId}
          channel="email"
          enabled={enabled}
        />
      </DeliveryChannelCard>
    </>
  );
}
